import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import {
  Calendar, Clock, FileCheck2, Mic, TrendingUp, Plus, ArrowUpRight, Timer,
} from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, suffix, testId }) => (
  <Card data-testid={testId} className="p-6 bg-af-card border-af rounded-md shadow-none hover:shadow-sm transition-all">
    <div className="flex items-start justify-between mb-4">
      <div className="h-10 w-10 rounded-md bg-af-subtle border border-af flex items-center justify-center">
        <Icon className="h-5 w-5 text-terracotta" />
      </div>
    </div>
    <div className="font-display font-extrabold text-4xl text-af leading-none">
      {value}
      {suffix ? <span className="text-lg ml-1 text-af-muted">{suffix}</span> : null}
    </div>
    <div className="text-sm text-af-secondary mt-2">{label}</div>
  </Card>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch((err) => console.error("Failed to load dashboard stats:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-af-muted">Chargement du tableau de bord…</div>;
  if (!stats) return <div className="p-8 text-af-muted">Impossible de charger les statistiques. Veuillez rafraîchir la page.</div>;

  const chartData = (stats.chart_last_7_days || []).map((d) => ({
    day: new Date(d.date).toLocaleDateString("fr-FR", { weekday: "short" }),
    réunions: d.count,
  }));

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div className="af-fade-up">
          <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Tableau de bord</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">
            Bonjour, {user?.full_name?.split(" ")[0] || "👋"}
          </h1>
          <p className="text-af-secondary mt-2">Voici ce qui se passe dans votre espace AfriMeet.</p>
        </div>
        <Link to="/app/meetings" data-testid="new-meeting-cta">
          <Button className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11 px-5">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle réunion
          </Button>
        </Link>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KpiCard icon={Calendar} label="Réunions totales" value={stats.total_meetings} testId="kpi-meetings" />
        <KpiCard icon={Mic} label="Minutes générées" value={stats.minutes_generated} suffix="min" testId="kpi-minutes" />
        <KpiCard icon={Timer} label="Temps économisé" value={stats.time_saved_hours} suffix="h" testId="kpi-time-saved" />
        <KpiCard icon={FileCheck2} label="Actions en attente" value={stats.pending_actions} testId="kpi-actions" />
      </div>

      {/* Chart + Status grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Card className="lg:col-span-2 p-6 bg-af-card border-af rounded-md shadow-none">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-display font-bold text-lg">Activité — 7 derniers jours</h3>
              <p className="text-xs text-af-muted mt-1">Nombre de réunions créées par jour</p>
            </div>
            <TrendingUp className="h-5 w-5 text-terracotta" />
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                <XAxis dataKey="day" stroke="#A8A29E" fontSize={12} />
                <YAxis stroke="#A8A29E" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E7E5E4", borderRadius: 6 }} />
                <Bar dataKey="réunions" fill="#B8862D" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 bg-af-card border-af rounded-md shadow-none">
          <h3 className="font-display font-bold text-lg mb-6">État des réunions</h3>
          <div className="space-y-4">
            {[
              { label: "Planifiées", value: stats.planned, color: "#D67D3E" },
              { label: "En cours", value: stats.in_progress, color: "#B8862D" },
              { label: "Terminées", value: stats.completed, color: "#3A2E1F" },
            ].map((row) => (
              <div key={row.label} data-testid={`status-row-${row.label.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-af-secondary">{row.label}</span>
                  <span className="font-display font-bold text-af">{row.value}</span>
                </div>
                <div className="h-1.5 w-full bg-af-subtle rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${stats.total_meetings ? (row.value / stats.total_meetings) * 100 : 0}%`,
                      background: row.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Recent summaries */}
      <Card className="p-6 bg-af-card border-af rounded-md shadow-none">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-display font-bold text-lg">Derniers comptes-rendus</h3>
            <p className="text-xs text-af-muted mt-1">Résumés générés par l'IA</p>
          </div>
          <Link to="/app/meetings" className="text-sm text-terracotta hover:underline flex items-center gap-1" data-testid="see-all-meetings">
            Voir tout <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {(!stats.recent_summaries || stats.recent_summaries.length === 0) ? (
          <div className="py-12 text-center">
            <div className="h-12 w-12 mx-auto rounded-md bg-af-subtle border border-af flex items-center justify-center mb-4">
              <Mic className="h-5 w-5 text-af-muted" />
            </div>
            <p className="text-af-secondary mb-4">Aucun compte-rendu pour l'instant.</p>
            <Link to="/app/meetings">
              <Button variant="outline" className="border-af rounded-md" data-testid="empty-create-meeting">
                Créer ma première réunion
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-[#E7E5E4]">
            {stats.recent_summaries.map((m) => (
              <Link to={`/app/meetings/${m.id}`} key={m.id}
                data-testid={`recent-summary-${m.id}`}
                className="block py-4 hover:bg-af-subtle -mx-6 px-6 transition-all">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-af mb-1 truncate">{m.title}</div>
                    <div className="text-sm text-af-secondary line-clamp-2 leading-relaxed">{m.executive_summary}</div>
                  </div>
                  <div className="text-xs text-af-muted whitespace-nowrap flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {Math.round((m.duration_seconds || 0) / 60)} min
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
